/**
 * Run `fn` after the first paint, when the browser is idle (never on the critical rendering path).
 *  - waits for two animation frames (the first frame with content has been committed), then
 *  - requestIdleCallback (with a timeout so it can't starve), or a plain timeout where it doesn't exist.
 * Returns a cancel function (safe to call after it ran).
 *
 * @param {() => void} fn
 * @param {{ timeout?: number }} [options]
 */
export function runWhenIdle(fn, { timeout = 3000 } = {}) {
  if (typeof window === "undefined") return () => {};
  let cancelled = false;
  let raf1 = 0;
  let raf2 = 0;
  let idleId = 0;
  let timerId = 0;

  const run = () => {
    if (cancelled) return;
    try {
      fn();
    } catch (e) {
      // third-party/optional work must never break the page
      // eslint-disable-next-line no-console
      console.warn("runWhenIdle task failed", e);
    }
  };
  const schedule = () => {
    if (cancelled) return;
    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(run, { timeout });
    } else {
      timerId = window.setTimeout(run, 200);
    }
  };

  if (typeof window.requestAnimationFrame === "function") {
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(schedule);
    });
  } else {
    schedule();
  }

  return () => {
    cancelled = true;
    if (raf1 && typeof window.cancelAnimationFrame === "function") window.cancelAnimationFrame(raf1);
    if (raf2 && typeof window.cancelAnimationFrame === "function") window.cancelAnimationFrame(raf2);
    if (idleId && typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idleId);
    if (timerId) window.clearTimeout(timerId);
  };
}
